import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Zap, Eye, Network, Lock, Wifi, Bell, Server, Settings,
  ArrowRight, Check, ChevronRight, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { getServices, getService } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { services as seoServices, cities } from '../data/seoPages';
import SEOHead from '../components/seo/SEOHead';

const iconMap = {
  Zap: Zap,
  Eye: Eye,
  Network: Network,
  Lock: Lock,
  Wifi: Wifi,
  Bell: Bell,
  Server: Server,
  Settings: Settings,
};

export default function Services() {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (slug) {
          const res = await getService(slug);
          setSelectedService(res.data);
        } else {
          const res = await getServices();
          setServices(res.data);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Service Detail View
  if (slug && selectedService) {
    const Icon = iconMap[selectedService.icon] || Zap;
    
    return (
      <div className="min-h-screen pt-24 pb-16 bg-white" data-testid="service-detail-page">
        {/* Hero - EXACT Abacus colors */}
        <section className="relative py-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 51, 102, 0.867) 50%, rgba(0, 166, 81, 0.25) 100%)' }}>
          {selectedService.image_url && (
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-10"
              style={{ backgroundImage: `url(${selectedService.image_url})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background" />
          
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <Link to="/services" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6">
                <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
                {t('services.title')}
              </Link>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white">
                    {language === 'es' ? selectedService.name_es : selectedService.name}
                  </h1>
                </div>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                {language === 'es' ? selectedService.description_es : selectedService.description}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/quote" data-testid="service-quote-btn">
                  <Button className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                    {t('hero.cta')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-heading font-bold text-white mb-8">Features & Capabilities</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {(language === 'es' ? selectedService.features_es : selectedService.features).map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border/50"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-foreground">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-8 md:p-12 text-center">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                  Ready to Get Started?
                </h2>
                <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                  Contact us today for a free consultation and quote. Our experts are ready to help.
                </p>
                <Link to="/quote" data-testid="service-cta-quote-btn">
                  <Button className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg">
                    Get Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  // EXACT 5 services from reference image with EXACT texts
  const staticServices = [
    {
      id: 'fiber-optic',
      slug: 'fiber-optic',
      name: 'Fibra Óptica',
      name_es: 'Fibra Óptica',
      description: 'Soluciones completas de fibra óptica para FTTH, FTTB y FTTP. Empalme por fusión, cables de fibra óptica para transmisión de datos de alta velocidad.',
      description_es: 'Soluciones completas de fibra óptica para FTTH, FTTB y FTTP. Empalme por fusión, cables de fibra óptica para transmisión de datos de alta velocidad.',
      icon: 'Zap',
      benefits: ['Alta Velocidad', 'Mayor Ancho de Banda', 'Mayor Distancia sin Pérdida', 'Inmunidad a Interferencias'],
      applications: ['Centros de Datos', 'Redes Corporativas', 'Redes FTTH', 'Infraestructura de Telecomunicaciones'],
    },
    {
      id: 'cctv',
      slug: 'cctv',
      name: 'CCTV y Videovigilancia',
      name_es: 'CCTV y Videovigilancia',
      description: 'Videovigilancia IP profesional: cámaras 4K/8MP, domos PTZ, imagen térmica y analíticos IA con LPR.',
      description_es: 'Videovigilancia IP profesional: cámaras 4K/8MP, domos PTZ, imagen térmica y analíticos IA con LPR.',
      icon: 'Eye',
      benefits: ['Monitoreo 24/7', 'Analíticos IA', 'Acceso Remoto', 'Almacenamiento Nube'],
      applications: ['Retail', 'Hotelería', 'Almacenes', 'Despliegues Multi-sitio'],
    },
    {
      id: 'structured-cabling',
      slug: 'structured-cabling',
      name: 'Cableado Estructurado',
      name_es: 'Cableado Estructurado',
      description: 'Diseño e instalación de infraestructura de cableado organizado para sistemas de voz, datos y telecomunicaciones.',
      description_es: 'Diseño e instalación de infraestructura de cableado organizado para sistemas de voz, datos y telecomunicaciones.',
      icon: 'Network',
      benefits: ['Infraestructura Organizada', 'Mantenimiento Fácil', 'Diseño Escalable', 'Estándares de la Industria'],
      applications: ['Centros de Datos', 'Grandes Superficies', 'Implementaciones Cat6/Cat6A/Cat7'],
    },
    {
      id: 'access-control',
      slug: 'access-control',
      name: 'Control de Acceso',
      name_es: 'Control de Acceso',
      description: 'Sistemas avanzados de control de acceso incluyendo lectores de tarjetas, sistemas biométricos y controladores de puertas.',
      description_es: 'Sistemas avanzados de control de acceso incluyendo lectores de tarjetas, sistemas biométricos y controladores de puertas.',
      icon: 'Lock',
      benefits: ['Seguridad Mejorada', 'Registro de Auditoría', 'Credenciales Móviles', 'Gestión en Nube'],
      applications: ['Acceso con tarjeta/llavero', 'Lectores biométricos', 'Gestión en la nube'],
    },
    {
      id: 'network-solutions',
      slug: 'network-solutions',
      name: 'Soluciones de Redes',
      name_es: 'Soluciones de Redes',
      description: 'Diseño e implementación de redes empresariales, WiFi corporativo, switches administrados y soluciones de conectividad.',
      description_es: 'Diseño e implementación de redes empresariales, WiFi corporativo, switches administrados y soluciones de conectividad.',
      icon: 'Wifi',
      benefits: ['WiFi Empresarial', 'Alta Disponibilidad', 'Gestión Centralizada', 'Seguridad de Red'],
      applications: ['Oficinas Corporativas', 'Hoteles', 'Hospitales', 'Centros Comerciales'],
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-white" data-testid="services-list-page">
      {/* SEO Head */}
      <SEOHead 
        title="Our Services"
        description="Professional low voltage services in Florida: Fiber Optic, CCTV, Structured Cabling, Access Control, Networking. Licensed contractor, 13+ years experience."
      />
      
      {/* Hero */}
      <section className="py-12 text-center" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 51, 102, 0.867) 50%, rgba(0, 166, 81, 0.25) 100%)' }}>
        <div className="container mx-auto px-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            {language === 'es' ? 'Nuestros Servicios' : 'Our Services'}
          </h1>
          <p className="text-white/70">
            {language === 'es' 
              ? 'Soluciones integrales de bajo voltaje para su negocio'
              : 'Comprehensive low voltage solutions for your business'}
          </p>
        </div>
      </section>

      {/* Services Cards - EXACT: 2 per row layout */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {staticServices.map((service, idx) => {
              const Icon = iconMap[service.icon] || Zap;
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm p-5"
                >
                  {/* Header with icon and title */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(0, 166, 81, 0.1)' }}>
                      <Icon className="w-5 h-5" style={{ color: '#00A651' }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold" style={{ color: '#003366' }}>
                        {service.name_es}
                      </h3>
                      <p className="text-gray-500 text-xs mt-1">
                        {service.description_es}
                      </p>
                    </div>
                  </div>

                  {/* Benefits and Applications - 2 columns */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs font-semibold text-orange-500 mb-2">BENEFICIOS</p>
                      {service.benefits.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                          <Check className="w-3 h-3 flex-shrink-0" style={{ color: '#00A651' }} />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-orange-500 mb-2">APLICACIONES</p>
                      {service.applications.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                          <Check className="w-3 h-3 flex-shrink-0" style={{ color: '#00A651' }} />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2">
                    <Link to={`/services/${service.slug}`} className="flex-1">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="w-full border-orange-500 text-orange-500 hover:bg-orange-50 text-xs"
                      >
                        Ver más →
                      </Button>
                    </Link>
                    <Link to="/quote" className="flex-1">
                      <Button 
                        size="sm"
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xs"
                      >
                        Solicitar Cotización
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Areas Hub - SEO Internal Linking */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
              {language === 'es' ? 'Áreas de Servicio en Florida' : 'Service Areas in Florida'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {language === 'es' 
                ? 'Ofrecemos todos nuestros servicios en las principales ciudades de Florida. Haz clic para ver información específica de tu área.'
                : 'We offer all our services across major Florida cities. Click to see location-specific information.'}
            </p>
          </div>
          
          <div className="grid gap-8">
            {/* Major Services with City Links */}
            {seoServices.slice(0, 5).map((service, serviceIdx) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: serviceIdx * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">
                    {language === 'es' ? service.nameEs : service.name}
                  </h3>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {cities.slice(0, 10).map((city) => (
                    <Link
                      key={city.slug}
                      to={`/${service.slug}-${city.slug}`}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-full text-sm transition-colors"
                      title={`${service.name} in ${city.name}, FL`}
                    >
                      {city.name}
                    </Link>
                  ))}
                  <Link
                    to={`/${service.slug}-miami`}
                    className="px-3 py-1.5 bg-orange-500 text-white rounded-full text-sm hover:bg-orange-600 transition-colors flex items-center gap-1"
                  >
                    {language === 'es' ? 'Ver todas →' : 'View all →'}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 166, 81, 0.25) 100%)' }}>
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">
            {language === 'es' ? '¿No está seguro de lo que necesita?' : 'Not Sure What You Need?'}
          </h2>
          <p className="text-white/70 mb-6">
            {language === 'es' 
              ? 'Nuestros expertos pueden evaluar sus necesidades y recomendar las mejores soluciones.'
              : 'Our experts can assess your needs and recommend the best solutions.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/quote">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                {language === 'es' ? 'Consulta Gratuita' : 'Free Consultation'}
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                {language === 'es' ? 'Contactar' : 'Contact Us'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
